import { ActivityAction } from "@/app/activity/activity-action"
import { ActivityDate } from "@/app/activity/activity-time"
import { ScannerLink } from "@/app/components/scanner-link"
import { WalletAddressLink } from "@/app/components/wallet-address"
import { PriceWrapper } from "@/app/price"
import { useScanner } from "@/app/sdk/anaconda-sdk-scanner"
import { formatDate } from "@/app/utils/format-date"
import type { Activity } from "@anaconda/core/activity/types"
import { Trans } from "@lingui/react/macro"
import {
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@rarible_int/anaconda_ui_components"
import { useNftReatom as useNft } from "../store/hooks.reatom"
import { Cell, HeaderCell } from "./cells"

interface Column {
  Header: React.ComponentType
  Content: React.ComponentType<Activity>
  Skeleton: React.ComponentType
}

export const COLUMNS: Column[] = [
  {
    Header: () => (
      <HeaderCell className="w-[80px]">
        <Trans>Action</Trans>
      </HeaderCell>
    ),
    Content: (activity) => (
      <Cell data-testid="action">
        <div className="flex items-center">
          <ActivityAction type={activity.type} />
        </div>
      </Cell>
    ),
    Skeleton: () => (
      <div className="flex items-center justify-end">
        <Skeleton className="h-[22px] w-[44px]" />
      </div>
    ),
  },
  {
    Header: () => (
      <HeaderCell className="w-[100px] text-right">
        <Trans>Value</Trans>
      </HeaderCell>
    ),
    Content: (activity) => (
      <Cell data-testid="price-value" className="text-right">
        {activity.type === "TRANSFER" ? (
          "-"
        ) : (
          <PriceWrapper {...activity.price} />
        )}
      </Cell>
    ),
    Skeleton: () => (
      <div className="flex items-center justify-end">
        <Skeleton className="h-6 w-[90px]" />
      </div>
    ),
  },
  {
    Header: () => (
      <HeaderCell className="w-[100px] text-right">
        <Trans>From</Trans>
      </HeaderCell>
    ),
    Content: (activity) => (
      <Cell data-testid="from" className="text-right">
        {activity.seller ? (
          <WalletAddressLink
            address={activity.seller}
            prefetch={false}
            className="justify-end"
          />
        ) : (
          "-"
        )}
      </Cell>
    ),
    Skeleton: () => (
      <div className="flex items-center justify-end">
        <Skeleton className="h-6 w-[90px]" />
      </div>
    ),
  },
  {
    Header: () => (
      <HeaderCell className="w-[100px] text-right">
        <Trans>To</Trans>
      </HeaderCell>
    ),
    Content: (activity) => (
      <Cell data-testid="to" className="text-right">
        <WalletAddressLink
          address={activity.buyer}
          prefetch={false}
          className="justify-end"
        />
      </Cell>
    ),
    Skeleton: () => (
      <div className="flex items-center justify-end">
        <Skeleton className="h-6 w-[90px]" />
      </div>
    ),
  },
  {
    Header: () => (
      <HeaderCell className="w-[100px] text-right">
        <Trans>Time</Trans>
      </HeaderCell>
    ),
    Content: (activity) => {
      const nft = useNft()
      const { getTransactionLink } = useScanner(nft.collection.blockchain)
      return (
        <Cell data-testid="date" className="text-right">
          <div className="flex items-center justify-end gap-2">
            <Tooltip>
              <TooltipTrigger>
                <ActivityDate date={new Date(activity.date)} />
              </TooltipTrigger>
              <TooltipContent>
                {formatDate(new Date(activity.date))}
              </TooltipContent>
            </Tooltip>
            <ScannerLink
              url={getTransactionLink(activity.transactionHash)}
              className="text-foreground/60"
            />
          </div>
        </Cell>
      )
    },
    Skeleton: () => <Skeleton className="h-6 w-8" />,
  },
]
